import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader, Loader, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function AdminPayments() {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ name: "", details: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/payment-methods").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list) return <Loader />;

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.details.trim()) { toast.error("Enter name and details"); return; }
    setBusy(true);
    try {
      await api.post("/admin/payment-methods", { ...form, enabled: true });
      toast.success("Payment method added"); setForm({ name: "", details: "" }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const update = async (m, patch) => {
    try { await api.put(`/admin/payment-methods/${m.id}`, { name: m.name, details: m.details, enabled: m.enabled, ...patch }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/admin/payment-methods/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <PageHeader title={t("payment_settings")} subtitle="These payment accounts appear in the member Deposit page instantly." />
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <form onSubmit={add} className="ec-card p-6 space-y-4">
          <h3 className="font-head font-semibold text-white flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" />Add Method</h3>
          <div><Label>Method Name</Label><Input data-testid="pm-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Easypaisa" /></div>
          <div><Label>Account Details</Label><Textarea data-testid="pm-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={3} className="mt-1" placeholder="Account title, number / IBAN / wallet address" /></div>
          <Button type="submit" disabled={busy} data-testid="pm-add" className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold px-8">{busy ? "..." : "Add"}</Button>
        </form>

        <div>
          <h3 className="font-head text-lg font-semibold text-white mb-4">Saved Methods</h3>
          {list.length === 0 ? <Empty label="No payment methods." /> : (
            <div className="space-y-3">
              {list.map((m) => (
                <div key={m.id} className="ec-card p-4" data-testid={`pm-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <Input value={m.name} onChange={(e) => setList(list.map((x) => x.id === m.id ? { ...x, name: e.target.value } : x))} className="mb-2 font-semibold" data-testid={`pm-name-${m.id}`} />
                      <Textarea value={m.details} onChange={(e) => setList(list.map((x) => x.id === m.id ? { ...x, details: e.target.value } : x))} rows={2} className="keep-ltr" data-testid={`pm-details-${m.id}`} />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <Switch checked={m.enabled} onCheckedChange={(v) => update({ ...m, enabled: v }, {})} data-testid={`pm-enabled-${m.id}`} />
                      <button onClick={() => remove(m.id)} className="text-red-400 p-1" data-testid={`pm-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => update(m, {})} data-testid={`pm-save-${m.id}`} className="mt-3 bg-white/5 border border-white/10 hover:bg-white/10">{t("save")}</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
